import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { ClipboardCheck, Check, FileText } from "lucide-react";

export function MoveOutRequirements({ propertyId, reloadSignal }) {
  const [checklist, setChecklist] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/properties/${propertyId}/moveout-checklist`);
      setChecklist(data || null);
    } catch (e) { console.error("load moveout checklist failed", e); }
    finally { setLoading(false); }
  }, [propertyId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (reloadSignal) load(); }, [reloadSignal, load]);

  const toggle = async (index) => {
    setChecklist((c) => ({ ...c, items: c.items.map((it, i) => i === index ? { ...it, done: !it.done } : it) }));
    try {
      await api.patch(`/moveout-checklist/${checklist.id}/items/${index}`);
    } catch (e) {
      console.error("toggle moveout item failed", e);
      setChecklist((c) => ({ ...c, items: c.items.map((it, i) => i === index ? { ...it, done: !it.done } : it) }));
    }
  };

  if (loading || !checklist) return null;
  const items = checklist.items || [];
  if (items.length === 0) return null;
  const doneCount = items.filter((i) => i.done).length;
  const pct = Math.round((doneCount / items.length) * 100);

  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 p-5 text-white" data-testid="moveout-requirements">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-brand/15 text-brand shrink-0"><ClipboardCheck size={20} /></span>
        <div className="min-w-0">
          <h3 className="font-heading font-semibold flex items-center gap-1.5">Move-out requirements
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/15 border border-amber-400/30 text-amber-100 text-[11px] font-semibold px-2 py-0.5"><FileText size={11} /> From your lease</span>
          </h3>
          <p className="text-sm text-white/60">Tick off exactly what your lease requires before you hand back the keys.</p>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <div className="flex-1 h-2.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full bg-brand transition-all" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-sm font-bold shrink-0" data-testid="moveout-progress">{doneCount} of {items.length} done</span>
      </div>

      <div className="mt-4 space-y-2">
        {items.map((it, i) => (
          <div key={`${i}-${(it.task || "").slice(0, 12)}`} data-testid={`moveout-item-${i}`}
            className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3.5 py-2.5">
            <button
              data-testid={`moveout-toggle-${i}`}
              onClick={() => toggle(i)}
              aria-pressed={it.done}
              className={`grid place-items-center h-5 w-5 rounded-md border shrink-0 transition-colors ${it.done ? "bg-emerald-500 border-emerald-500 text-white" : "border-white/30 hover:border-brand"}`}
            >
              {it.done && <Check size={13} strokeWidth={3} />}
            </button>
            <span className={`text-sm flex-1 min-w-0 ${it.done ? "text-white/40 line-through" : "text-white/90"}`}>{it.task}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
